import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import personIcon from "../assests/personIcon.svg";
import { useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../providers/auth-provider";
import BrandedLogo from "./branded-logo";
import SideBarData from "../utils/data/sideBar";
import clsx from "clsx";
import {
  faAngleLeft,
  faRightFromBracket,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useSidebar } from "../store/use-sidebar";
import ThemeToggle from "./theme-toggle";

export default function LeftSidebar() {
  const { user, HandleUserLogOut } = useContext(AuthContext);
  const { collapsed, toggleCollapsed, mobileOpen, closeMobile } = useSidebar();

  const isActive = (path: string) => {
    return window.location.pathname === path;
  };

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 hidden animate-scrim-in bg-black/60 backdrop-blur-sm smallLaptop:block"
          onClick={closeMobile}
        />
      )}

      <aside
        className={clsx(
          "group/sidebar relative flex h-full flex-col border-r border-edge bg-surface animate-fade-in-slow transition-[width,transform] duration-300 ease-out",
          collapsed ? "w-[84px]" : "w-[258px]",
          "smallLaptop:fixed smallLaptop:left-0 smallLaptop:top-0 smallLaptop:z-40 smallLaptop:h-screen smallLaptop:w-[260px]",
          mobileOpen
            ? "smallLaptop:translate-x-0"
            : "smallLaptop:-translate-x-full"
        )}
      >
        {/* ----------------------- HEADER / LOGO ----------------------- */}
        <div
          className={clsx(
            "flex items-center border-b border-edge-faint px-4 pb-4 pt-5 transition-all duration-300",
            collapsed
              ? "justify-center smallLaptop:justify-between"
              : "justify-between"
          )}
        >
          <div
            className={clsx(
              "flex items-center overflow-hidden transition-all duration-500 ease-out",
              collapsed
                ? "w-0 -translate-x-3 opacity-0 smallLaptop:w-full smallLaptop:translate-x-0 smallLaptop:opacity-100"
                : "w-full translate-x-0 opacity-100"
            )}
          >
            <BrandedLogo className="w-[104px] shrink-0 opacity-95" />
          </div>

          <button
            type="button"
            onClick={toggleCollapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label="Toggle sidebar"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-edge bg-overlay text-light-gray transition-all duration-200 hover:border-indigo-400/40 hover:text-text-primary hover:shadow-glow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50 smallLaptop:hidden"
          >
            <FontAwesomeIcon
              icon={faAngleLeft}
              className={clsx(
                "text-xs transition-transform duration-300",
                collapsed && "rotate-180"
              )}
            />
          </button>

          <button
            type="button"
            onClick={closeMobile}
            title="Close menu"
            aria-label="Close menu"
            className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-edge bg-overlay text-light-gray transition-colors duration-200 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50 smallLaptop:flex"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* ------------------------ NAV LIST --------------------------- */}
        <ul className="custom-scrollbar flex-1 overflow-y-auto overflow-x-hidden py-2 font-medium">
          {SideBarData.map((item) => {
            const active = isActive(item.route);

            return (
              <li
                key={item.route}
                className={clsx(
                  "group relative",
                  // Opens the second group, matching the reference rhythm
                  item.label === "My Customers" && "mt-6"
                )}
              >
                {/* The whole row is the link, so the icon is clickable too */}
                <Link
                  to={item.route}
                  onClick={closeMobile}
                  title={collapsed ? item.label : undefined}
                  aria-current={active ? "page" : undefined}
                  className={clsx(
                    "relative flex items-center gap-3.5 py-3 transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500/50",
                    active
                      ? "bg-active-background text-blue"
                      : "text-light-gray hover:bg-active-background/50 hover:text-text-primary",
                    collapsed
                      ? "justify-center px-0 smallLaptop:justify-start smallLaptop:px-6"
                      : "px-6"
                  )}
                >
                  {active ? (
                    <span
                      aria-hidden
                      className="absolute right-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-l-full bg-blue"
                    />
                  ) : null}

                  <FontAwesomeIcon
                    icon={item.icon}
                    className="w-6 shrink-0 text-center text-[17px]"
                  />

                  <span
                    className={clsx(
                      "flex-1 overflow-hidden whitespace-nowrap text-[15px]",
                      active ? "font-semibold" : "font-medium",
                      collapsed ? "hidden smallLaptop:inline" : "inline"
                    )}
                  >
                    {item.label}
                  </span>

                  {/* Collapsed rail: every item still names itself on hover */}
                  {collapsed ? (
                    <span
                      role="tooltip"
                      className="pointer-events-none absolute left-full z-50 ml-3 whitespace-nowrap rounded-lg border border-edge bg-surface-elevated px-3 py-1.5 text-[12px] font-medium text-text-primary opacity-0 shadow-overlay transition-opacity duration-200 group-hover:opacity-100 smallLaptop:hidden"
                    >
                      {item.label}
                    </span>
                  ) : null}
                </Link>

                {item?.subData && item.subData.length > 0 ? (
                  <ul
                    className={clsx(
                      "flex-col",
                      collapsed ? "hidden smallLaptop:flex" : "flex"
                    )}
                  >
                    {item.subData.map((subItem) => (
                      <li key={subItem.route}>
                        <Link
                          to={subItem.route}
                          onClick={closeMobile}
                          aria-current={
                            isActive(subItem.route) ? "page" : undefined
                          }
                          className={clsx(
                            "block truncate py-2 pl-[62px] pr-6 text-[14px] font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500/50",
                            isActive(subItem.route)
                              ? "text-blue"
                              : "text-light-gray hover:bg-active-background/50 hover:text-text-primary"
                          )}
                        >
                          {subItem.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>

        <ThemeToggle collapsed={collapsed} />

        {/* ------------------------- FOOTER ---------------------------- */}
        <button
          type="button"
          onClick={HandleUserLogOut}
          title="Log out"
          aria-label="Log out"
          className={clsx(
            "relative flex w-full items-center gap-3 border-t border-edge bg-surface-elevated px-4 py-3 text-left transition-colors duration-300 hover:bg-active-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500/50",
            collapsed && "justify-center smallLaptop:justify-start"
          )}
        >
          <img
            className="h-10 w-10 shrink-0 rounded-full ring-2 ring-indigo-500/30"
            src={personIcon}
            alt=""
          />

          <span
            className={clsx(
              "min-w-0 flex-1 flex-col",
              collapsed ? "hidden smallLaptop:flex" : "flex"
            )}
          >
            <span className="truncate text-[14px] font-medium text-heading-color">
              {user?.name}
            </span>
            <span className="truncate text-xs font-medium capitalize text-light-gray">
              {user?.type?.replace("-", " ")}
            </span>
          </span>

          <FontAwesomeIcon
            icon={faRightFromBracket}
            className={clsx(
              "shrink-0 text-sm text-light-gray opacity-70",
              collapsed ? "hidden smallLaptop:block" : "block"
            )}
          />
        </button>
      </aside>
    </>
  );
}
